import { useEffect, useId, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Plus, X } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, type ListBody } from "@/shared/api/client";
import { errorMessage } from "@/shared/i18n";
import { useCan } from "@/shared/lib/session";
import { quickCreateFor } from "./quickCreate";

export interface Option {
  id: string;
  label: string;
  sub?: string;
}

/**
 * Searchable single-select backed by a list endpoint (?q=). When the list is one the
 * user may add to (see quickCreate), a name that isn't there can be added on the spot.
 */
export function EntityPicker<T extends { id: string }>(props: {
  path: string;
  value: string | null | undefined;
  onChange: (id: string | null, row?: T) => void;
  toOption: (row: T) => Option;
  filter?: Record<string, string>;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  initialLabel?: string;
  allowFreeText?: boolean;
  onFreeText?: (text: string) => void;
  /** Non-crud endpoints that return a plain array. */
  plain?: boolean;
  /** false: never offer to add what was typed. */
  create?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [label, setLabel] = useState(props.initialLabel ?? "");
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const listId = useId();

  const { data, isFetching } = useQuery({
    queryKey: [props.path, "pick", q, props.filter],
    queryFn: async () => {
      if (props.plain) return (await api.get<T[]>(props.path)) ?? [];
      return (await api.get<ListBody<T>>(props.path, { q, limit: 12, filter: props.filter })).items;
    },
    // A plain list also names a preset value (it has no endpoint for one row).
    enabled: open || (!!props.plain && !!props.value && !label),
    staleTime: 30_000,
  });
  const rows = (data ?? []).filter((r) => !props.plain || !q || props.toOption(r).label.toLowerCase().includes(q.toLowerCase()));
  const can = useCan();
  const qc = useQueryClient();
  const creator = props.create === false || props.plain || props.allowFreeText ? undefined : quickCreateFor(props.path);
  const canCreate = !!creator && can(creator.perm) && !props.disabled;
  const typed = q.trim();
  // Only once the search for what was typed is back: offered sooner, a quick Enter or click adds a duplicate.
  const offerCreate = canCreate && typed !== "" && !isFetching && !rows.some((r) => props.toOption(r).label.trim() === typed);
  const [adding, setAdding] = useState(false);

  // Resolve the label of a preset value.
  const { data: current } = useQuery({
    queryKey: [props.path, "one", props.value],
    queryFn: () => api.get<T>(`${props.path}/${props.value}`),
    enabled: !!props.value && !props.initialLabel && !label && !props.plain,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (current) setLabel(props.toOption(current).label);
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const row = props.plain && props.value && !label ? (data ?? []).find((r) => r.id === props.value) : undefined;
    if (row) setLabel(props.toOption(row).label);
  }, [data, props.value]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!props.value && !props.allowFreeText) setLabel("");
  }, [props.value, props.allowFreeText]);
  useEffect(() => {
    const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (row: T) => {
    const o = props.toOption(row);
    setLabel(o.label);
    setQ("");
    setOpen(false);
    props.onChange(row.id, row);
  };

  const add = async () => {
    if (!creator || adding) return;
    setAdding(true);
    try {
      const row = await api.post<T>(props.path, creator.body(typed, props.filter));
      await qc.invalidateQueries({ queryKey: [props.path] });
      toast.success(t("quick.added", { name: typed }));
      pick(row);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={box} className="relative">
      <div className="relative">
        <input
          className={clsx("field pe-14", props.invalid && "border-danger")}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          disabled={props.disabled}
          placeholder={props.placeholder ?? t("common.select")}
          value={open ? q : label}
          onFocus={() => {
            setOpen(true);
            setQ(props.allowFreeText ? label : "");
          }}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
            if (props.allowFreeText) {
              setLabel(e.target.value);
              props.onFreeText?.(e.target.value);
              if (props.value) props.onChange(null);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - (offerCreate ? 0 : 1))); }
            if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === "Enter" && open && rows[active]) { e.preventDefault(); pick(rows[active]); }
            else if (e.key === "Enter" && open && offerCreate) { e.preventDefault(); void add(); }
            if (e.key === "Escape") setOpen(false);
            if (e.key === "Tab") setOpen(false);
          }}
        />
        <div className="pointer-events-none absolute inset-y-0 end-2 flex items-center gap-1 text-ink-3">
          {props.value && !props.disabled && (
            <button
              type="button"
              className="pointer-events-auto rounded p-0.5 hover:bg-line-2"
              aria-label={t("common.clear")}
              onClick={() => { setLabel(""); props.onChange(null); }}
            >
              <X className="size-3.5" />
            </button>
          )}
          <ChevronDown className="size-4" />
        </div>
      </div>
      {open && (rows.length > 0 || !props.allowFreeText || offerCreate) && (
        <ul id={listId} role="listbox" className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-[var(--radius-ctl)] border border-line bg-surface py-1 shadow-lg">
          {rows.length === 0 && !offerCreate && <li className="px-3 py-2 text-ink-3">{isFetching ? t("common.loading") : canCreate && !typed ? t("quick.hint") : t("common.noResults")}</li>}
          {rows.map((r, i) => {
            const o = props.toOption(r);
            return (
              <li
                key={r.id}
                role="option"
                aria-selected={r.id === props.value}
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                onMouseEnter={() => setActive(i)}
                className={clsx("cursor-pointer px-3 py-1.5", i === active && "bg-paper")}
              >
                <div>{o.label}</div>
                {o.sub && <div className="text-[12px] text-ink-3">{o.sub}</div>}
              </li>
            );
          })}
          {offerCreate && (
            <li
              role="option"
              aria-selected={false}
              aria-disabled={adding}
              onMouseDown={(e) => { e.preventDefault(); void add(); }}
              onMouseEnter={() => setActive(rows.length)}
              className={clsx("flex cursor-pointer items-center gap-1.5 border-t border-line-2 px-3 py-1.5 font-medium text-ledger", active === rows.length && "bg-paper")}
            >
              <Plus className="size-4 shrink-0" /> {t(creator!.label(props.filter), { name: typed })}
            </li>
          )}
          {canCreate && !typed && rows.length > 0 && <li className="border-t border-line-2 px-3 py-1.5 text-[12px] text-ink-3">{t("quick.hint")}</li>}
        </ul>
      )}
    </div>
  );
}

// Common pickers.
type Named = { id: string; name: string };
export const orgOption = (o: { id: string; name: string; tax_number?: string }) => ({ id: o.id, label: o.name, sub: o.tax_number || undefined });
export const userOption = (u: Named & { email?: string }) => ({ id: u.id, label: u.name, sub: u.email });
